import { useState } from "react";
import api from "../../utils/api";
import UploadImage from "../UploadImage";

export default function EditContract({ contract, onSuccess }) {
  const [contractName, setContractName] = useState(contract.contractName || "");
  const [signingDate, setSigningDate] = useState(
    contract.signingDate ? contract.signingDate.split("T")[0] : "",
  );
  const [expiryDate, setExpiryDate] = useState(
    contract.expiryDate ? contract.expiryDate.split("T")[0] : "",
  );
  const [artworks, setArtworks] = useState([...contract.artworks]);
  const [images, setImages] = useState([...contract.contractImageUrl]);
  const [newImages, setNewImages] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  const addArtworkField = () => setArtworks((prev) => [...prev, ""]);
  const updateArtwork = (idx, value) =>
    setArtworks((prev) => prev.map((a, i) => (i === idx ? value : a)));
  const removeArtwork = (idx) =>
    setArtworks((prev) => prev.filter((_, i) => i !== idx));

  const handleNewImageSelect = (file) => {
    const preview = URL.createObjectURL(file);
    setNewImages((prev) => [...prev, file]);
    setNewImagePreviews((prev) => [...prev, preview]);
  };
  const removeExistingImage = (idx) =>
    setImages((prev) => prev.filter((_, i) => i !== idx));
  const removeNewImage = (idx) => {
    setNewImages((prev) => prev.filter((_, i) => i !== idx));
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!contractName || !signingDate || !expiryDate || artworks.length === 0) {
      alert("All fields are required.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("contractName", contractName);
      formData.append("signingDate", signingDate);
      formData.append("expiryDate", expiryDate);
      formData.append("existingImages", JSON.stringify(images));
      formData.append("artworks", JSON.stringify(artworks));
      newImages.forEach((file) => formData.append("contractImageUrl", file));

      await api.patch(`/contracts/${contract._id}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Contract updated successfully!");
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err.response?.data || err.message);
      alert(err.response?.data?.message || err.message);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
    >
      <h2 className="text-xl font-semibold text-gray-800 mb-6">
        Edit Contract
      </h2>

      <div className="mb-4">
        <label className="block mb-1 font-medium text-gray-600">
          Contract Name
        </label>
        <input
          type="text"
          value={contractName}
          onChange={(e) => setContractName(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2"
        />
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-medium text-gray-600">
          Signing Date
        </label>
        <input
          type="date"
          value={signingDate}
          onChange={(e) => setSigningDate(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2"
        />
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-medium text-gray-600">
          Expiry Date
        </label>
        <input
          type="date"
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2"
        />
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-medium text-gray-600">Artworks</label>
        {artworks.map((art, idx) => (
          <div key={idx} className="flex gap-2 mb-2">
            <input
              type="text"
              value={art}
              onChange={(e) => updateArtwork(idx, e.target.value)}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2"
            />
            <button
              type="button"
              onClick={() => removeArtwork(idx)}
              className="px-3 py-2 bg-red-500 text-white rounded-lg"
            >
              &times;
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addArtworkField}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg"
        >
          + Add Artwork
        </button>
      </div>

      <div className="mb-6">
        <label className="block mb-1 font-medium text-gray-600">
          Contract Images
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {images.map((img, idx) => {
            const imgUrl = img.startsWith("http")
              ? img
              : `${process.env.REACT_APP_API_URL}${img}`;
            return (
              <div key={`existing-${idx}`} className="relative">
                <img
                  src={imgUrl}
                  alt={`Contract ${idx}`}
                  className="w-20 h-20 object-cover rounded cursor-pointer hover:scale-105 transition"
                  onClick={() => setSelectedImage(imgUrl)}
                  onError={(e) =>
                    (e.target.src = "https://via.placeholder.com/80?text=Error")
                  }
                />
                <button
                  type="button"
                  onClick={() => removeExistingImage(idx)}
                  className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  &times;
                </button>
              </div>
            );
          })}

          {newImagePreviews.map((preview, idx) => (
            <div key={`new-${idx}`} className="relative">
              <img
                src={preview}
                alt={`New ${idx}`}
                className="w-20 h-20 object-cover rounded cursor-pointer hover:scale-105 transition"
                onClick={() => setSelectedImage(preview)}
              />
              <button
                type="button"
                onClick={() => removeNewImage(idx)}
                className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
              >
                &times;
              </button>
            </div>
          ))}
        </div>

        <UploadImage onFileSelect={handleNewImageSelect} />
      </div>

      <button
        type="submit"
        className="w-full rounded-lg bg-blue-600 py-2.5 text-white font-medium hover:bg-blue-700"
      >
        Save Changes
      </button>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <img
            src={selectedImage}
            alt="Preview"
            className="max-h-[90%] max-w-[90%] rounded-lg shadow-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-5 right-5 text-white text-2xl font-bold"
          >
            ×
          </button>
        </div>
      )}
    </form>
  );
}
